import React from 'react';
import { useUser } from '@clerk/clerk-react';
import { UserTypeService } from '../lib/userTypeService';
import { isPublicRoute } from '../lib/routes';
import { hasRedirectLock, isOnboardingPath, peekPostAuthRedirect, clearClerkRedirectParams } from '../auth/postAuth';

interface AuthRedirectWrapperProps {
  children: React.ReactNode;
  onNavigate: (screen: string) => void;
}

const AuthRedirectWrapper: React.FC<AuthRedirectWrapperProps> = ({ children, onNavigate }) => {
  const { user, isSignedIn } = useUser();
  const [role, setRole] = React.useState<"contractor"|"homeowner"|"admin"|"unknown">("unknown");
  const [checked, setChecked] = React.useState(false);

  // Load role
  React.useEffect(() => {
    if (!isSignedIn || !user) {
      setChecked(true);
      return;
    }

    (async () => {
      // Block all auto-redirects during sensitive flows (e.g., payment)
      if ((window as any).__CB_BLOCK_AUTO_NAV) return;
      const path = window.location.pathname || '';
      if (/\/payment(-screen)?$/i.test(path)) return;
      // CB:BEGIN public-route-bypass
      // If current route is public (e.g., /kpi), do not perform any auth-based redirects
      // EXCEPTION: Root ("/") should still auto-redirect signed-in users to their dashboard
      if (isPublicRoute(path) && path !== '/') {
        return;
      }
      // CB:END public-route-bypass
      // If we're already on a dashboard URL, don't attempt any redirect again
      if (path === '/dashboard' || path === '/contractor-dashboard') {
        return;
      }
      // Suppress auto-redirect on interactive flows (so clicks don't get hijacked)
      const suppressPaths = [
        '/contractor-bids',
        '/contractor/projects', // CB:BEGIN suppress-project-details-redirect - Allow contractor project details page
        '/project-wizard',
        // CB:BEGIN homeowner-suppress-wizard-estimate
        '/wizard',
        '/estimate',
        // CB:END homeowner-suppress-wizard-estimate
        '/payment',
        '/payment-screen',
        '/next-steps',
        '/roof-report-coming-soon',
        '/contractor-verification',
        '/contractor-signup',
        '/settings'
      ];
      if (suppressPaths.some(p => path.startsWith(p))) {
        return;
      }
      const locked = hasRedirectLock();

      // *** CRITICAL GUARD ***
      // If we're on an onboarding page OR the redirect lock is set,
      // do not perform *any* auto-redirect (no demo fallback, no userType redirect).
      if (locked || isOnboardingPath(path)) {
        console.debug("AuthRedirectWrapper: suppressing auto-redirect (lock/onboarding).");
        return;
      }

      // If the app saved a post-auth redirect (edge case), honor it once.
      const saved = peekPostAuthRedirect();
      if (saved && path !== saved) {
        console.debug("AuthRedirectWrapper: honoring saved post-auth redirect:", saved);
        clearClerkRedirectParams();
        onNavigate(saved.replace('/', ''));
        return;
      }

      // From here on you may keep your *explicit* userType routing,
      // but DO NOT fallback to dashboard when userType is undefined.
      // That fallback is what caused the bounce.
      try {
        const userRole = await UserTypeService.getUserType(user);
        setRole(userRole);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setRole("unknown");
      } finally {
        setChecked(true);
      }
    })();
  }, [isSignedIn, user, onNavigate]);

  // FIX: Defer redirect until role resolved; never route contractors to homeowner pages
  React.useEffect(() => {
    if (!checked) return;
    const path = window.location.pathname;

    // Check admin first to prevent fallthrough to homeowner routes
    if (role === "admin") {
      localStorage.setItem("cb.usertype", "admin");
      const onNonAdminRoute = /^\/(home|homeowner|contractor|dashboard)/.test(path);
      if (onNonAdminRoute && !path.startsWith('/admin')) {
        onNavigate("/admin-dashboard", { replace: true });
      }
      return;
    }

    if (role === "contractor") {
      localStorage.setItem("cb.usertype", "contractor");
      const onHomeownerRoute = /^\/(home|homeowner)/.test(path);
      if (onHomeownerRoute) onNavigate("/contractor-dashboard", { replace: true });
      return;
    }

    if (role === "homeowner") {
      const onContractorRoute = /^\/contractor(\/|$)/.test(path);
      if (onContractorRoute) onNavigate("/dashboard", { replace: true });
      return;
    }

    // role === "unknown": do not redirect to avoid wrong jumps
  }, [checked, role, onNavigate]);

  // CB:BEGIN render-while-checking-contractor-routes
  // FIX: Allow contractor project details to render while auth is checking
  // Otherwise blank screen when navigating to /contractor/projects/:id
  const path = window.location.pathname || '';
  const isContractorProjectRoute = path.startsWith('/contractor/projects/');
  
  if (!checked && !isContractorProjectRoute) {
    return null; // avoid flicker for other routes
  }
  // CB:END render-while-checking-contractor-routes

  return <>{children}</>;
};

export default AuthRedirectWrapper; 